import { useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { UploadSimple, Eye, CheckCircle, Clock, CurrencyDollar, CreditCard } from "@phosphor-icons/react";

const statusColors = {
  pendiente: "bg-orange-500/20 text-orange-400 border-orange-500/30",
  en_revision: "bg-blue-500/20 text-blue-400 border-blue-500/30",
  pagada: "bg-green-500/20 text-green-400 border-green-500/30",
};

/**
 * Props:
 * - entryFee (number)
 * - uploadUrl (string) e.g. `/retas/{id}/upload-proof`
 * - proofUrl (string) e.g. `/retas/{id}/proof/{playerId}`
 * - approveUrl (string, optional admin) e.g. `/admin/retas/{id}/approve-payment/{playerId}`
 * - stripeCheckoutUrl (string, optional) e.g. `/retas/{id}/stripe-checkout` - enables card payment
 * - status ("pendiente" | "en_revision" | "pagada")
 * - canUpload (bool)
 * - canApprove (bool)
 * - onChange (() => void) to refresh parent
 * - testId (string)
 */
export default function PaymentUploader({
  entryFee = 0,
  uploadUrl,
  proofUrl,
  approveUrl,
  stripeCheckoutUrl,
  status = "pendiente",
  canUpload = false,
  canApprove = false,
  onChange,
  testId = "payment-uploader",
}) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [approving, setApproving] = useState(false);
  const [paying, setPaying] = useState(false);

  if (!entryFee || entryFee <= 0) return null;

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Archivo max 10MB");
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(uploadUrl, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Comprobante subido. En revision.");
      onChange && onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al subir");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const payWithCard = async () => {
    setPaying(true);
    try {
      const origin_url = window.location.origin;
      const { data } = await api.post(stripeCheckoutUrl, { origin_url });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.error("No se pudo iniciar el pago");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error iniciando pago");
    } finally {
      setPaying(false);
    }
  };

  const approve = async () => {
    setApproving(true);
    try {
      await api.post(approveUrl);
      toast.success("Pago aprobado");
      onChange && onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally {
      setApproving(false);
    }
  };

  const viewProof = async () => {
    try {
      const resp = await api.get(proofUrl, { responseType: "blob" });
      const url = window.URL.createObjectURL(resp.data);
      window.open(url, "_blank");
    } catch (err) {
      toast.error(err.response?.status === 404 ? "Sin comprobante" : "Error al ver comprobante");
    }
  };

  const Icon = status === "pagada" ? CheckCircle : status === "en_revision" ? Eye : Clock;

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid={testId}>
      <Badge variant="outline" className={`badge-status ${statusColors[status]} flex items-center gap-1`}>
        <CurrencyDollar size={10} weight="fill" />${entryFee}
        <Icon size={10} className="ml-1" />
        {status === "pagada" ? "Pagado" : status === "en_revision" ? "En revision" : "Pendiente"}
      </Badge>
      {canUpload && status !== "pagada" && stripeCheckoutUrl && (
        <Button
          type="button"
          size="sm"
          onClick={payWithCard}
          disabled={paying}
          className="bg-[#CCFF00] text-black hover:bg-[#B3E600] text-xs uppercase font-bold h-7"
          data-testid={`${testId}-stripe-btn`}
        >
          <CreditCard size={12} className="mr-1" />
          {paying ? "..." : "Pagar con tarjeta"}
        </Button>
      )}
      {canUpload && status !== "pagada" && (
        <>
          <input
            ref={fileRef}
            type="file"
            accept="image/*,.pdf"
            className="hidden"
            onChange={handleFileSelect}
            data-testid={`${testId}-file-input`}
          />
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="border-white/10 text-gray-400 hover:bg-white/5 text-xs uppercase font-semibold h-7"
            data-testid={`${testId}-upload-btn`}
          >
            <UploadSimple size={12} className="mr-1" />
            {uploading ? "Subiendo..." : status === "en_revision" ? "Re-subir" : "Transferencia"}
          </Button>
        </>
      )}
      {proofUrl && status !== "pendiente" && (
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={viewProof}
          className="text-gray-400 hover:text-[#CCFF00] text-xs h-7"
          data-testid={`${testId}-view-btn`}
        >
          <Eye size={12} className="mr-1" /> Ver
        </Button>
      )}
      {canApprove && approveUrl && status === "en_revision" && (
        <Button
          type="button"
          size="sm"
          onClick={approve}
          disabled={approving}
          className="bg-green-500/20 text-green-400 hover:bg-green-500/30 border border-green-500/30 text-xs uppercase font-bold h-7"
          data-testid={`${testId}-approve-btn`}
        >
          <CheckCircle size={12} className="mr-1" /> {approving ? "..." : "Aprobar"}
        </Button>
      )}
    </div>
  );
}
